import React from "react";
import { Link } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";

/**
 * Authority page targeted at search engines + AI overviews.
 * Intentionally minimal — clear hierarchy, no heavy design.
 */
export default function CurtainsDubaiCompany() {
  usePageMeta({
    title:
      "Curtain Company in Dubai | Custom Curtains & Installation — Time Furniture",
    description:
      "Time Furniture is a curtain company in Dubai providing custom-made curtains and blinds with measurement, fabrication, and installation services across the UAE.",
    canonical: "https://timefurniture.ae/curtains-dubai-company",
    ogImage: "/services/Ripplefold-curtains-timefurniture.webp",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      url: "https://timefurniture.ae/curtains-dubai-company",
      name: "Curtain Company in Dubai — Time Furniture",
      description:
        "Time Furniture is a curtain company in Dubai providing custom-made curtains and blinds with measurement, fabrication, and installation services across Dubai, Sharjah, Abu Dhabi and the wider UAE.",
      mainEntity: {
        "@type": "Service",
        name: "Custom Curtains and Blinds — Dubai",
        provider: { "@id": "https://timefurniture.ae/#organization" },
        areaServed: ["Dubai", "Sharjah", "Abu Dhabi", "United Arab Emirates"],
        serviceType: [
          "Sheer curtains",
          "Blackout curtains",
          "Ripple fold curtains",
          "Motorized curtains",
          "Roller blinds",
          "Zebra blinds",
          "Roman blinds",
        ],
      },
    },
  });

  return (
    <div data-testid="curtains-company-page" className="bg-[#FAF5EC]">
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Curtains in Dubai" },
        ]}
      />

      <article className="max-w-[1100px] mx-auto px-6 md:px-12 py-16 md:py-24 lg:py-28">
        {/* H1 */}
        <header className="mb-14 md:mb-20">
          <p className="eyebrow mb-5">Curtain Company · Dubai</p>
          <h1
            data-testid="cdc-h1"
            className="font-serif-display text-4xl md:text-6xl lg:text-[68px] leading-[1.05] tracking-tight text-[#1C110C]"
          >
            Curtain Company in Dubai —{" "}
            <em className="text-[#6E1F2C] not-italic">
              Custom Curtains &amp; Installation
            </em>
            .
          </h1>
        </header>

        {/* 1. Introduction */}
        <section data-testid="cdc-intro" className="mb-14 md:mb-20">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-5">
            Introduction
          </h2>
          <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px] max-w-3xl">
            Time Furniture is a curtain company in Dubai providing custom-made
            curtains and blinds with measurement, fabrication, and installation
            services.
          </p>
        </section>

        {/* 2. What We Offer */}
        <section data-testid="cdc-offer" className="mb-14 md:mb-20">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-5">
            What We Offer
          </h2>
          <ul className="space-y-2 text-[#1C110C]">
            {[
              "Sheer curtains",
              "Blackout curtains",
              "Ripple fold curtains",
              "Motorized curtains",
              "Blinds (roller, zebra, roman)",
            ].map((it) => (
              <li
                key={it}
                className="text-base md:text-[17px] leading-[1.85] before:content-['—'] before:text-[#D4A963] before:mr-3"
              >
                {it}
              </li>
            ))}
          </ul>
        </section>

        {/* 3. Why Choose Time Furniture */}
        <section data-testid="cdc-why" className="mb-14 md:mb-20">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-5">
            Why Choose Time Furniture
          </h2>
          <ul className="space-y-2 text-[#1C110C]">
            {[
              "Established in 1993",
              "30+ years experience",
              "In-house production (curtains, upholstery, joinery)",
              "UAE-wide service",
            ].map((it) => (
              <li
                key={it}
                className="text-base md:text-[17px] leading-[1.85] before:content-['—'] before:text-[#D4A963] before:mr-3"
              >
                {it}
              </li>
            ))}
          </ul>
        </section>

        {/* 4. Areas We Serve */}
        <section data-testid="cdc-areas" className="mb-14 md:mb-20">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#1C110C] mb-5">
            Areas We Serve
          </h2>
          <p className="text-[#3E2A22] leading-[1.85] text-base md:text-[17px] max-w-3xl">
            Dubai, Sharjah, Abu Dhabi, and across the UAE.
          </p>
        </section>

        {/* 5. CTA */}
        <section data-testid="cdc-cta" className="pt-6 border-t border-[#6E1F2C]/20">
          <Link
            to="/curtains-dubai#curtain-quote"
            data-testid="cdc-cta-link"
            className="inline-flex items-center gap-3 bg-[#6E1F2C] text-white px-9 py-5 text-[11px] tracking-[0.32em] uppercase hover:bg-[#56151F] transition-colors"
          >
            Get Curtain Quote →
          </Link>
          <p className="mt-6 text-sm text-[#7A6B5F]">
            Or explore the full{" "}
            <Link
              to="/curtains-dubai"
              className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
            >
              curtains and blinds collection
            </Link>
            .
          </p>
        </section>
      </article>
    </div>
  );
}
